<template>
  <v-row :class="{ 'd-inline-block mx-0 flex-grow-0': !thumbnails }">
    <div v-if="thumbnails" class="d-flex flex-wrap flex">
      <v-col
        v-if="uploadable"
        :cols="buttonWidth || buttonHeight ? 12 : cols"
        class="d-flex justify-center align-center"
      >
        <div
          class="box"
          :style="{
            height: buttonHeight ? buttonHeight + 'px' : '100%',
            paddingTop: buttonHeight ? buttonHeight : '100%',
          }"
        >
          <media-upload
            :is-android="isAndroid"
            :thumbnail="true"
            :folder="folder"
            :compress="compress"
            :limitation="limitation"
            :button-height="buttonHeight"
            :disabled="disabled"
            :uploading.sync="uploading"
            :button-text="$t(buttonText)"
            :on-change="imageUploaded"
          />
        </div>
      </v-col>
      <div
        ref="imgsContainerRef"
        class="imgs-container"
        :style="draggable ? 'display: flex' : 'display: contents'"
      >
        <v-col
          v-for="img in formattedImages"
          :key="img.original"
          :cols="cols"
          class="p-relative"
        >
          <div v-if="selectable" class="image-select">
            <v-checkbox
              v-model="selectedImages"
              :value="img.original"
              color="white"
              dense
              hide-details
              @change="emitSelection"
            />
          </div>
          <div
            v-if="img.original !== 'loading'"
            class="box"
            :style="{
              paddingTop: buttonHeight ? buttonHeight : '100%',
            }"
          >
            <v-tooltip v-if="img.class" bottom color="warning">
              <template #activator="{ on }">
                <div class="d-flex" v-on="on">
                  <ImageView
                    :readonly="readonly"
                    :on-remove="removeImg"
                    class="hover-image-view"
                    :class="img.class"
                    :meta="imageMeta(img.original)"
                    :src="img.thumbnail || img.original"
                    small-width="100%"
                    @click="imageClicked(img.original)"
                  />
                </div>
              </template>
              <span class="text-caption">Too small, min 1024x683</span>
            </v-tooltip>
            <ImageView
              v-else
              :readonly="readonly"
              :on-remove="removeImg"
              class="hover-image-view"
              :meta="imageMeta(img.original)"
              :caption="img.caption"
              :src="img.thumbnail || img.original"
              small-width="100%"
              @click="imageClicked(img.original)"
            />
          </div>
          <div v-else class="box">
            <v-skeleton-loader width="100%" height="100%" type="image" />
          </div>
        </v-col>
      </div>
    </div>
    <span v-else class="pr-2 text-caption pointer" @click.stop="imageClicked"
      ><v-icon small :color="iconColor">{{ icon }} </v-icon
      ><span v-if="!hideFileCount">({{ formattedImages.length || 0 }})</span>
    </span>
    <v-dialog
      v-if="dialog"
      v-model="dialog"
      content-class="rounded-xl"
      class="white"
      :width="zoomIn ? 1600 : 500"
      @click:outside="currImage = null"
    >
      <v-sheet class="rounded-xl overflow-auto">
        <v-carousel
          v-model="currImage"
          :hide-delimiters="formattedImages.length > 10"
          height="auto"
        >
          <v-carousel-item
            v-for="slide in formattedImages"
            :key="slide.original"
            class="pb-10"
            :value="slide.original"
          >
            <div class="pa-3 d-flex justify-space-between">
              <v-btn
                v-if="!readonly"
                class="white top-left"
                fab
                icon
                x-small
                @click.stop="rotate(slide.original)"
              >
                <v-icon color="darkpurple">$rotate</v-icon>
              </v-btn>
              <v-btn
                class="white bottom-left"
                fab
                icon
                x-small
                @click.stop="downloadItem(slide.original)"
              >
                <v-icon color="darkpurple">fas fa-arrow-alt-circle-down</v-icon>
              </v-btn>

              <CopyClipboard color="darkpurple" :text="slide.original" />
              <v-btn
                v-if="listingId"
                class="white top-right"
                fab
                icon
                x-small
                @click="showInfoModal(slide.original)"
              >
                <v-icon color="darkpurple">fas fa-home</v-icon>
              </v-btn>
              <v-btn
                v-if="!readonly"
                class="white top-right"
                fab
                icon
                x-small
                @click.stop="remove(slide.original)"
              >
                <v-icon color="warning">$trash</v-icon>
              </v-btn>
            </div>
            <iframe
              v-if="isPdf(slide.original)"
              width="100%"
              height="700px"
              :src="`${slide.original}`"
            />
            <!-- Instruction videos pruned from Cloudinary storage survive in
                 the S3 backup — the raw src 404s, cloudinaryDelivery resurrects
                 them and re-containers .mov for browsers that can't decode
                 QuickTime (#26365). The PDF branch above stays on the raw src:
                 f_auto would rasterize the document into a JPEG of page 1. -->
            <video
              v-else-if="
                isVideo(slide.original) && currImage === slide.original
              "
              width="100%"
              autoplay
              controls
              height="500"
              :src="cloudinaryDelivery(slide.original)"
              @error="
                $event.target.src !== slide.original &&
                  ($event.target.src = slide.original)
              "
            />
            <v-img
              v-else
              :class="{ 'image-zoom': zoomIn }"
              class="zoom-hover"
              contain
              :src="cloudinaryDelivery(slide.original)"
              @click="zoom"
            >
            </v-img>
            <div
              v-if="showCaption"
              class="d-flex pa-1 my-2 align-center flex-column"
            >
              <div class="pa-1 d-flex col-12">
                <v-textarea
                  v-model="slide.caption"
                  label="Caption"
                  rows="3"
                  dense
                  outlined
                  class="mr-1"
                  hide-details
                  @input="debouncedInput(slide, 'caption')"
                ></v-textarea>
              </div>
              <div
                class="pa-1 d-flex col-12 justify-space-between align-center"
              >
                <v-textarea
                  v-model="slide.ai_caption"
                  label="AI-suggested Caption"
                  dense
                  outlined
                  rows="3"
                  readonly
                  class="mr-1"
                  hide-details
                  @input="debouncedInput(slide, 'ai_caption')"
                ></v-textarea>
                <v-btn
                  v-if="slide.ai_caption"
                  icon
                  small
                  color="purple"
                  @click="copyAiCaption(slide)"
                >
                  <v-icon small>fas fa-arrow-up</v-icon>
                </v-btn>
              </div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </v-sheet>
    </v-dialog>
  </v-row>
</template>

<script>
import CommonFunctions from 'components/mixins/common_functions'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import ImageView from 'components/common/image-view'
import CopyClipboard from 'components/copy-clipboard'
import MediaUpload from 'components/common/media-upload.vue'
import { mapGetters } from 'vuex'
import get from 'lodash/fp/get'
import isEmpty from 'lodash/fp/isEmpty'
import isString from 'lodash/fp/isString'
import Sortable from 'sortablejs'

export default {
  name: 'Gallery',
  components: { MediaUpload, CopyClipboard, ImageView },
  mixins: [CommonFunctions, PermissionsMixin],
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: 'Upload',
    },
    buttonWidth: {
      type: [String, Number],
    },
    buttonHeight: {
      type: [String, Number],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    thumbnails: {
      type: Boolean,
      default: false,
    },
    uploadable: {
      type: Boolean,
      default: false,
    },
    rotateImage: {
      type: Function,
      default: () => ({}),
    },
    limitation: {
      type: Function,
    },
    validator: {
      type: Function,
    },
    removeImg: {
      type: Function,
      default: () => ({}),
    },
    icon: {
      type: String,
    },
    listingId: {
      type: Number,
    },
    iconColor: {
      type: String,
    },
    folder: {
      type: String,
    },
    cols: {
      type: Number,
      default: 6,
    },
    onChange: {
      type: Function,
      default: () => ({}),
    },
    compress: {
      type: Boolean,
      default: true,
    },
    draggable: {
      type: Boolean,
      default: false,
    },
    showCaption: {
      type: Boolean,
      default: false,
    },
    hideFileCount: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hovered: false,
      zoomIn: false,
      uploading: false,
      dialog: false,
      currImage: '',
      imgsContainerRef: null,
      selectedImages: this.value,
    }
  },
  mounted() {
    if (this.draggable) {
      Sortable.create(this.$refs.imgsContainerRef, {
        animation: 150,
        onUpdate: this.onDragged,
        ghostClass: 'ghost',
      })
    }
  },
  computed: {
    ...mapGetters(['currentUser', 'currentListingTask']),
    imagesLength() {
      return this.images && this.images.length
    },
    formattedImages() {
      if (this.images.length && isString(this.images[0])) {
        return this.images.map(img => ({ original: img }))
      }
      if (this.validator) {
        return this.images.map(img => ({
          ...img,
          class: this.validator(img) ? 'invalid-resolution' : '',
        }))
      }
      return this.images
    },
    getDeviceType() {
      const userAgent = window.navigator.userAgent
      if (/Android/i.test(userAgent)) {
        return 'Android'
      } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
        return 'iPhone'
      } else {
        return 'Unknown'
      }
    },
    isAndroid() {
      return this.getDeviceType === 'Android'
    },
  },
  watch: {
    uploading(val) {
      if (val) {
        this.$emit('upload-started')
      }
    },
    imagesLength(value, oldValue) {
      if (!value) {
        this.dialog = false
      }
    },
    value(newVal) {
      this.selectedImages = newVal
    },
    selectedImages(newVal) {
      this.$emit('input', newVal)
    },
  },
  methods: {
    showInfoModal(src) {
      this.$store.commit('showModal', {
        name: 'InfoItemModal',
        props: { listingId: this.listingId, imageUrl: src },
      })
    },
    imageMeta(img) {
      return get(['media_meta', img], this.currentListingTask)
    },
    imageClicked(img) {
      if (isEmpty(this.images)) {
        return
      }
      this.currImage = img
      this.dialog = true
    },
    hoverPic() {
      this.hovered = true
    },
    zoom() {
      this.zoomIn = !this.zoomIn
    },
    outPic() {
      this.hovered = false
    },
    rotate(oldLink) {
      this.rotateImage(oldLink)(this.rotateImg(oldLink))
    },
    remove(link) {
      this.removeImg(link)
    },
    imageUploaded(images) {
      this.onChange(images)
    },
    onDragged(e) {
      this.$emit('on-dragged', e)
    },
    debouncedInput(image, field) {
      this.images.find(a => a.original === image.original)[field] = image[field]
      this.$emit('caption-change')
    },
    emitSelection() {
      this.$emit('selection-change', this.selectedImages)
    },
    copyAiCaption(slide) {
      const image = this.images.find(a => a.original === slide.original)
      if (image) {
        this.$set(image, 'caption', slide.ai_caption)
        slide.caption = slide.ai_caption
        this.$emit('caption-change')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.hover-image-view:hover {
  cursor: pointer;
}

.zoom-hover:hover {
  cursor: zoom-in;
}

.zoom-hover.image-zoom:hover {
  cursor: zoom-out;
}
.invalid-resolution {
  border: 2px solid red;
}
.box {
  width: 100%;
  position: relative;

  > div {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
}
.imgs-container {
  flex-wrap: wrap;
  width: 100%;
}
.ghost {
  opacity: 0.1;
}
.image-select {
  z-index: 2;
  top: 10px;
  border-radius: 4px;
  position: absolute;
  left: 16px;
}
:deep() .v-input--selection-controls__input {
  color: white;
}
</style>
